import * as React from 'react';
import styled from '@emotion/styled';
import H3 from '@base/H3/H3';
import P1 from '@base/P1/P1';
import { Fonts, Spacings } from '@styles/index';
import Button from '@base/Button/Button';
import Image from '@base/Image';
import H4 from '@base/H4/H4';
import P2 from '@base/P2/P2';
import pushRoute from '@utils/pushRoute';
import useBatchDetails from '@api/hooks/batch/useBatchDetails';
import { useRouter } from 'next/router';
import useFetch from '@hooks/useFetch';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import Link from '@base/Link/Link';
import { HOST_TYPE } from '@utils/api-helper/host';

interface IBatchJoinSuccessDrawerProps {
  open: boolean;
  onManageBatch: () => void;
}

const StyH3 = styled(H3)`
  font-weight: ${Fonts.HEAVY};
  line-height: 28px;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyP1 = styled(P1)`
  font-weight: ${Fonts.LIGHT};
  line-height: 18px;
  margin-top: ${Spacings.SPACING_1B};
  margin-bottom: ${Spacings.SPACING_6B};
`;

const StyP2 = styled(P2)`
  font-weight: ${Fonts.LIGHT};
  line-height: 18px;
  margin-top: ${Spacings.SPACING_3B};
`;

const InfoWrapper = styled.div`
  max-width: 332px;
`;

const OuterWrapper = styled.div`
  grid-column: span 10;
`;

const Card = styled.div`
  margin-top: ${Spacings.SPACING_6B};
  padding: ${Spacings.SPACING_8B};
  background: var(--color-base-1);
  box-shadow: ${boxShadowGenerator()};
  border-radius: ${Spacings.SPACING_2B};
`;

const StyH4 = styled(H4)`
  font-weight: ${Fonts.SEMIBOLD};
  line-height: 24px;
  margin-top: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_1B};
`;

const BatchJoinSuccessDrawer: React.FC<IBatchJoinSuccessDrawerProps> = ({
  open,
  onManageBatch
}) => {
  const { query = {} } = useRouter();
  const { batchUID } = query;
  const { batchDetails = {} } = useBatchDetails(batchUID);
  const { goal = {} } = batchDetails;

  const { data = {}, mutate: refetchBatchCount } = useFetch(
    open
      ? 'v2/topology/users/TMUVD/self/?actions=[enrolled_batch_count]'
      : null,
    { hostType: HOST_TYPE.BACKEND_API_HOST }
  );
  const { enrolledBatchCount } = data;

  React.useEffect(() => {
    refetchBatchCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onClick = () => {
    onManageBatch();
  };

  if (!open) return null;

  return (
    <>
      <OuterWrapper>
        <Wrapper>
          <InfoWrapper>
            <StyH3 color="var(--color-text-primary)">
              You’ve joined the batch
            </StyH3>
            <StyP2 color="var(--color-text-secondary)">
              Classes will be updated on your planner shortly
            </StyP2>
          </InfoWrapper>
          <Image
            src="batch-success.svg"
            height={Spacings.SPACING_20B}
            width={Spacings.SPACING_20B}
            alt="batch"
          />
        </Wrapper>
        <Card>
          <Image
            src="batch-manage.svg"
            height={Spacings.SPACING_20B}
            width={Spacings.SPACING_20B}
            alt="batch"
          />
          <StyH4 color="var(--color-text-primary)">{`You’re now in ${enrolledBatchCount} batches`}</StyH4>
          <StyP1 color="var(--color-text-secondary)">
            We strongly recommend leaving from the batches you don’t need
          </StyP1>
          <Link
            href={`/goal/${goal.slug}/${goal.uid}/enrolled?showBatches=true`}
          >
            <Button
              // @ts-ignore
              label="Manage batches"
              color="black"
              variant="filled"
              onClick={onClick}
            />
          </Link>
        </Card>
      </OuterWrapper>
    </>
  );
};

export default BatchJoinSuccessDrawer;
